import {
  TECH_CHECK_MESSAGE_SOURCE,
  TECH_CHECK_MESSAGE_VERSION,
  TechCheckEventPayloads,
  TechCheckEventType,
  TechCheckMessage,
} from './events';

/**
 * Determines whether the application is currently running inside an iframe.
 * Accessing `window.top` can throw in cross-origin scenarios, so we defensively
 * treat any error as "embedded" (the safest assumption for a widget that is
 * meant to be embedded).
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Sends a single, versioned event message to the parent window.
 *
 * Communication is intentionally one-way (iframe -> parent) and the target
 * origin is `'*'` because the tech check is a public-facing widget that exposes
 * no sensitive data. When the app is not embedded in an iframe this is a no-op.
 *
 * The payload argument is only required for event types that carry data; the
 * overloads below enforce this at the type level.
 */
export function postMessageToParent<T extends keyof TechCheckEventPayloads>(
  type: T,
  ...args: TechCheckEventPayloads[T] extends void ? [] : [payload: TechCheckEventPayloads[T]]
): void {
  // Nothing to talk to when we're the top-level window.
  if (!isEmbedded()) {
    return;
  }

  const message: TechCheckMessage<TechCheckEventType> = {
    source: TECH_CHECK_MESSAGE_SOURCE,
    version: TECH_CHECK_MESSAGE_VERSION,
    type,
    timestamp: Date.now(),
    // `args[0]` is undefined for void-payload events, which is the intended shape.
    payload: args[0] as TechCheckEventPayloads[T],
  };

  try {
    window.parent.postMessage(message, '*');
  } catch {
    // Never let a messaging failure break the tech check itself.
  }
}
